import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "hooks/use-dispatch";
import { useSelector } from "hooks/use-selector";
import { setPolicyData } from "store/quote";
import { selectIsTestUser, selectUpdatedQuoteDto } from "store/quote/selectors";
import { type UserPolicy } from "types/quote-types";
import { BadPaymentResponseError } from "utils/error/bad-payment-response";
import { ProviderScriptLoadError } from "utils/error/provider-script-load";
import { sentryCaptureError } from "utils/tracking/sentry";
import { getFeatureVariation } from "utils/experiments/cookie-features";
import {
  CollapsableSection,
  type CollapsableSectionRef,
  type CollapsableSectionProps,
} from "../collapsable-section";
import { usePaymentTracking, useTrackPaymentMade } from "../tracking";
import { ErrorPopup } from "./error-popup";
import { type PaymentFormProps, type PaymentMethod } from "./payment-form";
import { PaymentForm } from "./styles";
import { getErrorKey } from "./payment-form/checkout/errors";
import { type PaymentService } from "utils/api/payment";
export type PaymentChangeStatus = "completed" | "failed" | "requested";

export interface PaymentSectionProps
  extends Pick<
    CollapsableSectionProps,
    "active" | "disabled" | "onActivate" | "showTitleBadge"
  > {
  amendPaymentHash?: string;
  disablePaymentForm: boolean;
  onCreditCardChange: PaymentFormProps["onCreditCardChange"];
  onPaymentChange: (status: PaymentChangeStatus) => void;
  policyCodeHash?: string;
  totalPrice?: number;
  provider?: PaymentService;
  userPolicy?: UserPolicy;
}

export function PaymentSection({
  active,
  amendPaymentHash,
  disablePaymentForm,
  onCreditCardChange,
  onPaymentChange,
  policyCodeHash,
  totalPrice,
  provider,
  userPolicy,
  ...props
}: PaymentSectionProps) {
  const dispatch = useDispatch();
  const isTestUser = useSelector(selectIsTestUser);
  const updatedQuoteDto = useSelector(selectUpdatedQuoteDto);
  const paypal = Boolean(!provider || provider === "payPal");
  const paymentProvider =
    provider ??
    (getFeatureVariation("paymentProvider") === "cko" ? "checkOut" : "stripe");

  const { t } = useTranslation("screens", {
    keyPrefix: "checkout.sections.payment",
  });
  const collapsableSectionRef = useRef<CollapsableSectionRef | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [error, setError] = useState<{
    description?: string;
    footer?: string;
  } | null>(null);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    null,
  );

  const trackPaymentMade = useTrackPaymentMade();

  function errorFormatter(errorArg: unknown, provider?: string) {
    const error: { description?: string; footer?: string } = {};

    if (errorArg instanceof BadPaymentResponseError) {
      error.description =
        errorArg.details?.code && provider === "checkOut"
          ? t(getErrorKey(errorArg.details.code))
          : (errorArg.details?.description ??
            (errorArg.details?.code
              ? `Error code: ${errorArg.details.code}`
              : errorArg.message));

      error.footer = errorArg.details?.footer;
    } else {
      error.description =
        typeof errorArg === "string"
          ? errorArg
          : String((errorArg as { message?: unknown })?.message ?? errorArg);
    }

    return error;
  }

  usePaymentTracking(
    active &&
      !amendPaymentHash &&
      loaded &&
      !!updatedQuoteDto &&
      totalPrice !== undefined,
  );

  if ((!amendPaymentHash && !updatedQuoteDto) || totalPrice === undefined) {
    return null;
  }

  return (
    <CollapsableSection
      active={active && loaded}
      ref={collapsableSectionRef}
      {...props}
      completed={false}
      loading={!loaded}
      title={t("title")}
    >
      <PaymentForm
        active={active && loaded}
        disabled={
          disablePaymentForm && {
            applePay: paymentMethod !== "applePay",
            creditCard: paymentMethod !== "creditCard",
            googlePay: paymentMethod !== "googlePay",
            payPal: paymentMethod !== "payPal",
          }
        }
        onCreditCardChange={onCreditCardChange}
        onError={(error) => {
          if (error instanceof ProviderScriptLoadError) {
            sentryCaptureError(error, {
              contexts: { provider: { providerType: error.provider } },
              name: "Provider_script_failed",
            });
          } else {
            sentryCaptureError(error);
          }
          throw error;
        }}
        onPaymentComplete={(quotePaymentResponse) => {
          if (quotePaymentResponse) {
            dispatch(setPolicyData(quotePaymentResponse));
            trackPaymentMade(paymentMethod, {
              uniqueUuid: quotePaymentResponse.userPolicy.uniqueUuid,
            });
          }

          onPaymentChange("completed");
        }}
        onPaymentError={(errorArg, silent) => {
          const error = errorFormatter(errorArg, paymentProvider);

          sentryCaptureError(errorArg, {
            contexts: { payment: { paymentType: paymentMethod } },
            name: "Payment_failed",
          });

          setPaymentMethod(null);
          trackPaymentMade(paymentMethod, { error: error.description });

          if (!silent) {
            setError(error);
          }

          onPaymentChange("failed");
        }}
        onPaymentRequest={(method) => {
          setPaymentMethod(method);
          onPaymentChange("requested");
        }}
        onReady={() => {
          setLoaded(true);
          collapsableSectionRef.current?.updateContentHeight();
        }}
        paymentData={{
          ...(amendPaymentHash
            ? { amendPaymentHash, policyCodeHash }
            : { quoteDto: updatedQuoteDto }),
          quoteId: userPolicy?.id,
          totalPrice,
        }}
        paypal={paypal}
        provider={paymentProvider}
        showCreditCardExtraFields={
          getFeatureVariation("creditCardExtraFields") === "active"
        }
        test={!!isTestUser}
      />

      <ErrorPopup
        {...error}
        onClose={() => {
          setError(null);
        }}
        open={error !== null}
      />
    </CollapsableSection>
  );
}
